import PageScaffold from "../../components/PageScaffold";

import { useContext, useEffect, useState } from "react";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import NumbersList from "../../components/numbers_page/NumbersList";
import AuthenticationContext from "../../contexts/auth/AuthenticationContext";
import { formatNumber } from '@leedpulse-monorepo/utils';
import {
  UserRole,
  EChannel,
  ENumberProducts,
  channelAsString,
  numberProductsAsString,
} from '@leedpulse-monorepo/enums';
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import searchNumbers from "../../../domain/use_cases/numbers/searchNumbers";
import { INumberView } from "../../../domain/interfaces/INumberView";

export const NUMBERS_PAGE_PATH = "/numbers";
export const NUMBERS_PAGE_TITLE = "Numéros";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface NumbersPageProps { }

const NumbersPage: React.FC<NumbersPageProps> = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthenticationContext);

  const [numbers, setNumbers] = useState<INumberView[]>([]);
  const [page, setPage] = useState<number>(0);
  const [nbHits, setNbHits] = useState<number>(0);
  const [exactNbHits, setExactNbHits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hitsPerPage, setHitsPerPage] = useState<number>(20);

  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [productConstraint, setProductConstraint] = useState<ENumberProducts | undefined>(() => {
    const p = searchParams.get('product');
    return p ? p as ENumberProducts : undefined;
  });
  const [channelConstraint, setChannelConstraint] = useState<EChannel | undefined>(
    () => {
      const channel = searchParams.get('channel');
      return channel ? channel as EChannel : undefined;
    }
  );
  const [availabilityConstraint, setAvailabilityConstraint] = useState<boolean | undefined>(() => {
    const available = searchParams.get('available');
    return available ? available === 'true' : undefined;
  });

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
      authContext.authentication?.role
    );

  async function searchNumber(pageToFetch = 0, newHitsPerPage = hitsPerPage) {
    setIsLoading(true);
    try {
      const useCaseResponse = await searchNumbers(
        authContext.authentication,
        query,
        pageToFetch,
        newHitsPerPage,
        {
          available: availabilityConstraint,
          channel: channelConstraint,
          product: productConstraint,
        }
      );

      setNumbers(useCaseResponse.hits);
      setNbHits(useCaseResponse.nbHits);
      setExactNbHits(useCaseResponse.exactNbHits);
      setPage(pageToFetch);
    } catch (err) {
      console.error('[Algolia] erreur :', err);
    } finally {
      setIsLoading(false);
    }
  }
  
  useEffect(() => {
    const timer = setTimeout(() => {
      searchNumber(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, productConstraint, channelConstraint, availabilityConstraint]);

  const updateFilterSearchParams = ({
    product,
    channel,
    available,
  }: {
    product?: ENumberProducts;
    channel?: EChannel;
    available?: boolean;
  }) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (product) {
        newParams.set('product', product);
      } else {
        newParams.delete('product');
      }

      if (channel) {
        newParams.set('channel', channel);
      } else {
        newParams.delete('channel');
      }

      if (available !== undefined) {
        newParams.set('available', String(available));
      } else {
        newParams.delete('available');
      }

      return newParams;
    });
  };

  const handleChangeProductConstraint = (product: ENumberProducts | undefined) => {
    updateFilterSearchParams({
      product,
      channel: channelConstraint,
      available: availabilityConstraint,
    });
    setProductConstraint(product);
  };

  const handleChangeChannelConstraint = (channel: EChannel | undefined) => {
    updateFilterSearchParams({
      product: productConstraint,
      channel,
      available: availabilityConstraint,
    });
    setChannelConstraint(channel);
  };

  const handleChangeAvailabilityConstraint = (available: boolean | undefined) => {
    updateFilterSearchParams({
      product: productConstraint,
      channel: channelConstraint,
      available,
    });
    setAvailabilityConstraint(available);
  };

  return (
    <PageScaffold
      title={NUMBERS_PAGE_TITLE + ' (' + formatNumber(nbHits) + ')'}
      subtitle={"Gérez les numéros"}
      breadcrumbItems={[
        {
          title: "Numéros",
        },
      ]}
      controlsSettings={{
        searchLabel: "Numéro de téléphone",
        onSearch: (value) => setQuery(value ?? ''),
        isLoading: false,
        onSearchCancel: () => setQuery(''),
        actionButtons: !isReadOnly
          ? [
            {
              label: "Nouveau numéro",
              type: "primary",
              props: {
                icon: <PlusOutlined />,
                onClick: () => navigate("new"),
              },
            },
            {
              label: 'Rafraîchir',
              type: "primary",
              props: {
                icon: <ReloadOutlined />,
                onClick: () => searchNumber(page),
              },
            },
          ]
          : [
            {
              label: <ReloadOutlined />,
              type: "primary",
              props: {
                onClick: () => searchNumber(page),
              },
            },
          ],
      }}
      filterSettings={[
        {
          placeholder: 'Filtrer par produit',
          clearable: true,
          style: { width: 150 },
          onFilter: (filter) => {
            handleChangeProductConstraint(filter as ENumberProducts | undefined);
          },
          selectedValue: productConstraint,
          values: Object.values(ENumberProducts).map((role) => ({
              label: `${numberProductsAsString(role)}`,
              value: role,
            })),
        },
        {
          placeholder: 'Filtrer par type',
          clearable: true,
          style: { width: 150 },
          onFilter: (filter) => {
            handleChangeChannelConstraint(filter as EChannel | undefined);
          },
          selectedValue: channelConstraint,
          values: Object.values(EChannel).map((channel) => ({
              label: `${channelAsString(channel)}`,
              value: channel,
            })),
        },
        {
          placeholder: 'Filtrer par disponibilité',
          clearable: true,
          style: { width: 150 },
          onFilter: (filter) => {
            handleChangeAvailabilityConstraint(filter === undefined ? undefined : filter === 'true');
          },
          selectedValue: availabilityConstraint === undefined ? undefined : String(availabilityConstraint),
          values: [
            { label: 'Disponible', value: 'true' },
            { label: 'Attribué', value: 'false' },
          ],
        },
      ]}
    >
      <NumbersList
        numbers={numbers}
        query={query}
        exactNbHits={exactNbHits}
        loading={isLoading}
        page={page}
        nbHits={nbHits}
        hitsPerPage={hitsPerPage}
        onPageChange={(p) => searchNumber(p)}
        onPageSizeChange={(newPageSize) => {
          setHitsPerPage(newPageSize);
          searchNumber(0, newPageSize);
        }}
      />
    </PageScaffold>
  );
};

export default NumbersPage;
